import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { getStaffDetailPath, getStaffReturnPath, getStaffSection } from '../../app/router/staffNavigation';
import { HOME_ROUTE, STAFF_UPDATE_ROUTE } from "../../app/router/routes";
import { useAppSelector } from "../../app/store/store";
import { LayoutPages } from "../layoutPages";
import { FEATURES, FEATURE_SECTIONS, hasFeature, myService, useMyActions } from "../../entities/my";
import { IUserPersonalDataUpdate, userService } from "../../entities/user";
import { MyError } from "../../shared/lib/error/MyError";
import { AuthError } from "../../shared/lib/error/AuthError";
import { useGlobalMessageActions } from "../../entities/globalMessage";
import { LoaderSpinner } from "../../shared/ui/spinner";
import { MyInput } from "../../shared/ui/input";
import { MyButton } from "../../shared/ui/button";
import { ChooseItems } from "../../features/chooseItems";
import classes from "./staffUpdate.module.scss";
import { workTgService } from "../../entities/workTg";
import { IItem } from "../../shared/model/types";
import { WorkTgSelector } from "../../features/workTgSelector";
import { tutorService } from "../../entities/tutor";
import { IStudentTag, studentService } from "../../entities/student";
import { TagSelector } from "../../features/tagSelector";

const emptyForm: IUserPersonalDataUpdate = {
    user_id: 0,
    full_name: '',
    email: '',
    phone: '',
    tg: '',
    is_active: false,
    cost_per_hour: '',
    cost_per_shift: '',
    cost_per_month: '',
    desired_hours: '',
    tag_ids: [],
    gender: '',
}

export default function StaffUpdatePage() {
    const navigate = useNavigate()
    const {pathname, search} = useLocation()
    const section = getStaffSection(pathname)
    const backPath = getStaffReturnPath(search, section)
    const {user_id} = useParams<{user_id: string}>()
    const parsedUserId = Number(user_id)
    const isValidUserId = Boolean(user_id) && !Number.isNaN(parsedUserId) && parsedUserId > 0

    const {my} = useAppSelector(s => s.myReducer)
    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const canOpenStaffSection = hasFeature(my, FEATURE_SECTIONS.STAFF)
    const canReadStaff = hasFeature(my, FEATURES.IDM.STAFF_READ, parsedUserId)
    const canUpdatePersonalData = hasFeature(my, FEATURES.IDM.PERSONAL_UPDATE, parsedUserId)
    const canUpdateTutor = hasFeature(my, FEATURES.TUTORS.UPDATE, parsedUserId)
    const canAssignStaffTags = hasFeature(my, FEATURES.IDM.STAFF_TAGS_ASSIGN, parsedUserId)
    const canListWorkTgs = hasFeature(my, FEATURES.WORK_TGS.LIST)
    const canUpdateWorkTgs = hasFeature(my, FEATURES.IDM.WORK_TGS_UPDATE, parsedUserId)
    const canEditWorkTgs = canListWorkTgs && canUpdateWorkTgs
    const canReadAvailableFeatures = hasFeature(my, FEATURES.IDM.USER_AVAILABLE_FEATURES_LIST, parsedUserId)
    const canOpenEditor = canOpenStaffSection
        && canReadStaff
        && (canUpdatePersonalData || canEditWorkTgs || canUpdateTutor)

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isSaving, setIsSaving] = useState<boolean>(false)
    const [form, setForm] = useState<IUserPersonalDataUpdate>(emptyForm)
    const [isTutor, setIsTutor] = useState<boolean>(false)
    const [isCoordinator, setIsCoordinator] = useState<boolean>(false)
    const [isCurator, setIsCurator] = useState<boolean>(false)
    const [isHr, setIsHr] = useState<boolean>(false)
    const [selectedWorkTgs, setSelectedWorkTgs] = useState<IItem[]>([])
    const [initialWorkTgs, setInitialWorkTgs] = useState<IItem[]>([])
    const [canSelectMultipleWorkTgs, setCanSelectMultipleWorkTgs] = useState<boolean>(false)
    const [tags, setTags] = useState<IStudentTag[]>([])

    const canEditTutorData = isTutor && canUpdateTutor
    const isShiftStaff = isCoordinator || isCurator
    const canEditTargetWorkTgs = canListWorkTgs && (canUpdateWorkTgs || canEditTutorData)
    const canEditTarget = canUpdatePersonalData || canEditWorkTgs || canEditTutorData

    const handleError = (e: unknown, defaultMessage: string) => {
        console.log(e)
        if(e instanceof AuthError){
            setIsAuth(false)
            setGlobalMessage({message: e.message, type: 'error'})
        }
        else if(e instanceof MyError){
            setGlobalMessage({message: e.message, type: 'error'})
        }
        else{
            setGlobalMessage({message: defaultMessage, type: 'error'})
        }
    }

    const setField = <K extends keyof IUserPersonalDataUpdate>(field: K, value: IUserPersonalDataUpdate[K]) => {
        setForm(prev => ({
            ...prev,
            [field]: value,
        }))
    }

    useEffect(() => {
        if(!canOpenEditor || !isValidUserId){
            return
        }

        const loadData = async () => {
            try{
                setIsLoading(true)
                const user = await userService.getPersonalData(parsedUserId)
                const targetIsTutor = user.roles.some(role => {
                    const roleName = role.role_name.trim().toLowerCase()
                    const roleCode = role.role_code.trim().toLowerCase()
                    return roleName.includes('репетитор') || roleName.includes('tutor') || roleCode.includes('tutor')
                })
                const targetIsCoordinator = user.roles.some(role => {
                    const roleName = role.role_name.trim().toLowerCase()
                    const roleCode = role.role_code.trim().toLowerCase()
                    return roleName.includes('координатор') || roleName.includes('coordinator') || roleCode.includes('coordinator')
                })
                const targetIsCurator = user.roles.some(role => {
                    const roleName = role.role_name.trim().toLowerCase()
                    const roleCode = role.role_code.trim().toLowerCase()
                    return roleName.includes('куратор') || roleName.includes('curator') || roleCode.includes('curator')
                })
                const targetIsHr = user.roles.some(role => {
                    const roleName = role.role_name.trim().toLowerCase()
                    const roleCode = role.role_code.trim().toLowerCase()
                    return roleCode === 'hr' || roleName === 'hr' || roleName.includes('hr')
                })
                const canEditTutorByCurrentRight = targetIsTutor && canUpdateTutor
                const canEditCurrentWorkTgs = canListWorkTgs && (canUpdateWorkTgs || canEditTutorByCurrentRight)
                const [assignedWorkTgs, availableFeatures, tagsRes, tutorData] = await Promise.all([
                    canUpdateWorkTgs
                        ? workTgService.getAssignedToUser(parsedUserId)
                        : Promise.resolve(user.work_tgs),
                    canEditCurrentWorkTgs && canReadAvailableFeatures
                        ? myService.getFeatureKeys(parsedUserId)
                        : Promise.resolve([]),
                    canAssignStaffTags ? studentService.getTags() : Promise.resolve([]),
                    targetIsTutor ? tutorService.getByUserId(parsedUserId) : Promise.resolve(null),
                ])
                const desiredHours = tutorData?.desired_hours || ''
                const gender = tutorData?.gender || user.gender || ''
                setForm({
                    user_id: user.user_id,
                    full_name: user.full_name || '',
                    email: user.email || '',
                    phone: user.phone || '',
                    tg: user.tg || '',
                    is_active: Boolean(user.is_active),
                    cost_per_hour: user.cost_per_hour || '',
                    cost_per_shift: user.cost_per_shift || '',
                    cost_per_month: user.cost_per_month || '',
                    desired_hours: desiredHours,
                    tag_ids: user.tag_ids || [],
                    gender,
                })
                setIsTutor(targetIsTutor)
                setIsCoordinator(targetIsCoordinator)
                setIsCurator(targetIsCurator)
                setIsHr(targetIsHr)
                const normalizedAssignedWorkTgs = assignedWorkTgs.map(workTg => ({id: workTg.id, name: workTg.name}))
                setSelectedWorkTgs(normalizedAssignedWorkTgs)
                setInitialWorkTgs(normalizedAssignedWorkTgs)
                setCanSelectMultipleWorkTgs(availableFeatures.includes(FEATURES.WORK_TGS.MULTIPLE_AVAILABLE))
                setTags(tagsRes)
            }
            catch(e){
                if(e instanceof MyError && (e.status === 404 || e.status >= 500)){
                    navigate(backPath, {replace: true})
                    setGlobalMessage({message: 'Сотрудник не найден', type: 'error'})
                    return
                }
                handleError(e, 'Ошибка при получении данных сотрудника')
            }
            finally{
                setIsLoading(false)
            }
        }

        loadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canOpenEditor, isValidUserId, parsedUserId])

    const onSave = async () => {
        if((canUpdatePersonalData || canEditTutorData) && !form.full_name.trim()){
            setGlobalMessage({message: 'Введите ФИО сотрудника', type: 'error'})
            return
        }
        if(isTutor && form.desired_hours.trim() && (!/^\d+$/.test(form.desired_hours.trim()) || Number(form.desired_hours.trim()) <= 0)){
            setGlobalMessage({message: 'Желаемые часы в неделю должны быть положительным целым числом', type: 'error'})
            return
        }

        try{
            setIsSaving(true)
            if(canUpdatePersonalData){
                await userService.updatePersonalData({
                    ...form,
                    full_name: form.full_name.trim(),
                    email: form.email.trim(),
                    phone: form.phone.trim(),
                    tg: form.tg.trim(),
                    cost_per_hour: isTutor ? form.cost_per_hour.trim() : '',
                    cost_per_shift: isShiftStaff ? (form.cost_per_shift || '').trim() : '',
                    cost_per_month: isHr ? form.cost_per_month.trim() : form.cost_per_month,
                    tag_ids: isTutor && canAssignStaffTags ? form.tag_ids : [],
                    gender: form.gender,
                })
            }
            const selectedWorkTgIds = selectedWorkTgs.map(item => Number(item.id)).filter(id => id > 0)
            const initialWorkTgIds = initialWorkTgs.map(item => Number(item.id)).filter(id => id > 0)
            if(canEditTutorData){
                await tutorService.update({
                    id: form.user_id,
                    user_id: form.user_id,
                    full_name: form.full_name.trim(),
                    email: form.email.trim(),
                    phone: form.phone.trim(),
                    tg: form.tg.trim(),
                    cost_per_hour: form.cost_per_hour.trim(),
                    tag_ids: canAssignStaffTags ? form.tag_ids : [],
                    work_tg_name: selectedWorkTgs[0]?.name || initialWorkTgs[0]?.name || '',
                    work_tg_ids: canEditTargetWorkTgs ? selectedWorkTgIds : initialWorkTgIds,
                    desired_hours: form.desired_hours.trim(),
                    gender: form.gender,
                })
            }
            if(canEditWorkTgs){
                const idsToAdd = selectedWorkTgIds.filter(id => !initialWorkTgIds.includes(id))
                const idsToRemove = initialWorkTgIds.filter(id => !selectedWorkTgIds.includes(id))

                await Promise.all([
                    ...idsToAdd.map(id => workTgService.addToUser(form.user_id, id)),
                    ...idsToRemove.map(id => workTgService.removeFromUser(form.user_id, id))
                ])
            }
            setGlobalMessage({message: 'Данные сотрудника успешно обновлены', type: 'ok'})
            navigate(getStaffDetailPath(form.user_id, section, search), {replace: true})
        }
        catch(e){
            handleError(e, 'Ошибка при обновлении данных сотрудника')
        }
        finally{
            setIsSaving(false)
        }
    }

    const selectedTagItems = useMemo<IItem[]>(
        () => tags
            .filter(tag => form.tag_ids.includes(tag.id))
            .map(tag => ({id: tag.id, name: tag.tag_name})),
        [form.tag_ids, tags]
    )

    if(!canOpenEditor || !isValidUserId || (!isLoading && !canEditTarget)){
        return <Navigate to={HOME_ROUTE.path} replace />
    }

    return (
        <LayoutPages
            title={STAFF_UPDATE_ROUTE.name}
            titleLink={getStaffDetailPath(parsedUserId, section, search)}
        >
            {
                isLoading
                    ?
                <section className={classes.loader}>
                    <LoaderSpinner />
                </section>
                    :
                <section className={classes.container}>
                    {(canUpdatePersonalData || canEditTutorData) && <section className={classes.card}>
                        <section className={classes.title}>Персональные данные</section>
                        <section className={classes.fields}>
                            <MyInput
                                title="ФИО"
                                value={form.full_name}
                                setValue={value => setField('full_name', value)}
                                placeholder="Введите ФИО сотрудника"
                            />
                            <MyInput
                                title="Email"
                                value={form.email}
                                setValue={value => setField('email', value)}
                                placeholder="Введите email"
                            />
                            <MyInput
                                title="Телефон"
                                value={form.phone}
                                setValue={value => setField('phone', value)}
                                placeholder="Введите телефон"
                            />
                            <MyInput
                                title="Telegram"
                                value={form.tg}
                                setValue={value => setField('tg', value)}
                                placeholder="Введите ссылку или username"
                            />
                        </section>
                    </section>}

                    {
                        canEditTargetWorkTgs && !isTutor &&
                        <section className={classes.card}>
                            <section className={classes.title}>Доступные ТГ</section>
                            <section className={classes.fields}>
                                <WorkTgSelector
                                    getData={async () => {
                                        const workTgs = await workTgService.getAll()
                                        return workTgs.map(workTg => ({id: workTg.id, name: workTg.name}))
                                    }}
                                    selectedItems={selectedWorkTgs}
                                    onChange={setSelectedWorkTgs}
                                    multiple={canSelectMultipleWorkTgs}
                                    placeholder={canSelectMultipleWorkTgs ? 'Выберите доступные ТГ' : 'Выберите доступный ТГ'}
                                />
                            </section>
                        </section>
                    }

                    {
                        (canUpdatePersonalData || canEditTutorData) && isTutor &&
                        <section className={classes.card}>
                            <section className={classes.title}>Данные исполнителя</section>
                            <section className={classes.fields}>
                                <MyInput
                                    title="Стоимость часа"
                                    value={form.cost_per_hour}
                                    setValue={value => setField('cost_per_hour', value)}
                                    placeholder="Например: 600"
                                />
                                <MyInput
                                    title="Желаемые часы в неделю"
                                    type="number"
                                    min="1"
                                    step="1"
                                    inputMode="numeric"
                                    value={form.desired_hours}
                                    setValue={value => setField('desired_hours', value)}
                                    placeholder="Например: 20"
                                />
                                <ChooseItems
                                    title="Пол"
                                    selectedItems={form.gender === 'male' ? [1] : form.gender === 'female' ? [2] : []}
                                    setItem={id => setField('gender', id === 1 ? 'male' : id === 2 ? 'female' : '')}
                                    getData={async () => [
                                        {id: 1, name: 'Мужской'},
                                        {id: 2, name: 'Женский'},
                                    ]}
                                />
                                {
                                    canAssignStaffTags &&
                                    <TagSelector
                                        title="Теги"
                                        selectedItems={selectedTagItems}
                                        onChange={items => setField('tag_ids', items.map(item => Number(item.id)).filter(id => id > 0))}
                                        getData={async () => tags.map(tag => ({id: tag.id, name: tag.tag_name}))}
                                        canCreate
                                        createPlaceholder="Например: ОГЭ"
                                        onCreate={async (tagName) => {
                                            const createdTag = await studentService.createTag(tagName)
                                            setTags(prev => [...prev.filter(tag => tag.id !== createdTag.id), createdTag]
                                                .sort((first, second) => first.tag_name.localeCompare(second.tag_name)))
                                            return {id: createdTag.id, name: createdTag.tag_name}
                                        }}
                                    />
                                }
                            </section>
                        </section>
                    }

                    {
                        (canUpdatePersonalData || canEditTutorData) && isShiftStaff &&
                        <section className={classes.card}>
                            <section className={classes.title}>
                                {isCoordinator ? 'Данные координатора' : 'Данные куратора'}
                            </section>
                            <section className={classes.fields}>
                                <MyInput
                                    title="Стоимость смены"
                                    value={form.cost_per_shift || ''}
                                    setValue={value => setField('cost_per_shift', value)}
                                    placeholder="Например: 3500"
                                />
                            </section>
                        </section>
                    }

                    {
                        canUpdatePersonalData && isHr &&
                        <section className={classes.card}>
                            <section className={classes.title}>Данные HR</section>
                            <section className={classes.fields}>
                                <MyInput
                                    title="Зарплата в месяц"
                                    value={form.cost_per_month}
                                    setValue={value => setField('cost_per_month', value)}
                                    placeholder="Например: 70000"
                                />
                            </section>
                        </section>
                    }

                    <section className={classes.actions}>
                        <MyButton onClick={onSave} isLoading={isSaving}>
                            Сохранить
                        </MyButton>
                    </section>
                </section>
            }
        </LayoutPages>
    )
}
